// Logo motion hero, coded with GSAP (timeline + SplitText). Choreography from the logo video:
//   0.30s  illustration fades up
//   0.60s  wordmark letters spring up one by one, then the orange dot pops (elastic)
//   then   tagline flips letter by letter (like componentry.dev "flipping word swap"):
//          "One store for" stays put, the ending flips through TAG below, repeating
//   then   the orange "paw" flips through SWAP below (thecattherapy, thedogtherapy, ...) and back,
//          shrinking the wordmark a little when a longer word would not fit
// On scroll (hero pinned, scrubbed): the illustration splits down the middle into a cat half and a
//   dog half, which part and flip over into "For Cats" / "For Dogs" cards (like componentry.dev
//   "scroll split card"). Each half/card opens Shop all filtered to that pet.
// Clicking the wordmark replays the letters. Real text throughout (SEO + screen readers).
// No GSAP or reduced motion: the final layout is shown straight away.
(function () {
  const { useEffect, useRef } = React;
  const h = React.createElement;
  const BASE = window.ASSET_BASE || "";
  const G = window.gsap;
  if (G && window.SplitText) G.registerPlugin(window.SplitText);   // register once, at load
  if (G && window.ScrollTrigger) G.registerPlugin(window.ScrollTrigger);

  const PREFIX = "One store for";
  const TAG = [
    "every pet need.",
    "your pet's style.",
    "the latest pet products.",
    "pet care, not just carts.",
  ];
  // Dog / cat cards on the back of the split illustration. Set img to an asset file name to use a photo.
  const PET_CARDS = [                                   // order = left half, right half of the picture
    { pet: "cat", kicker: "Shop by pet", title: "For Cats", text: "Teasers, brushes, litter scoops & more for curious cats.", cta: "Shop cats", img: "pet-card-cats.webp", alt: "An orange-and-white cat, a silver tabby and a fluffy blue-eyed cat" },
    { pet: "dog", kicker: "Shop by pet", title: "For Dogs", text: "Harnesses, leads, toys & grooming for every walk and play.", cta: "Shop dogs", img: "pet-card-dogs.webp", alt: "A golden retriever, a French bulldog and a beagle sitting together" },
  ];
  const PAW_D = "M12 21c-2.2 0-4.6-1.3-4.6-3.6 0-2.4 2.3-4.9 4.6-4.9s4.6 2.5 4.6 4.9c0 2.3-2.4 3.6-4.6 3.6ZM5.2 12.6c-1.2.3-2.5-.7-2.9-2.3-.4-1.6.2-3.1 1.4-3.4 1.2-.3 2.5.7 2.9 2.3.4 1.6-.2 3.1-1.4 3.4Zm13.6 0c1.2.3 2.5-.7 2.9-2.3.4-1.6-.2-3.1-1.4-3.4-1.2-.3-2.5.7-2.9 2.3-.4 1.6.2 3.1 1.4 3.4ZM9.1 8.7c-1.3.1-2.4-1.3-2.5-3.1-.1-1.8.9-3.4 2.2-3.5 1.3-.1 2.4 1.3 2.5 3.1.1 1.8-.9 3.4-2.2 3.5Zm5.8 0c1.3.1 2.4-1.3 2.5-3.1.1-1.8-.9-3.4-2.2-3.5-1.3-.1-2.4 1.3-2.5 3.1-.1 1.8.9 3.4 2.2 3.5Z";
  function petCount(pet) {
    const all = [].concat(...Object.values((window.DATA && window.DATA.PRODUCTS) || {}));
    return new Set(all.filter((p) => p.pets && p.pets.includes(pet)).map((p) => p.id)).size;
  }

  const SWAP = ["cat", "dog", "grooming", "toy", "walk"];      // words that take turns with "paw"
  const flipChars = (text, layer) => Array.from(text).map((c, i) =>
    h("span", { key: layer + i, className: "hm-fc hm-fc-" + layer }, c === " " ? "\u00a0" : c));

  function PtMotionHero({ onNav }) {
    const root = useRef(null);
    const word = useRef(null);     // wordmark timeline, kept for replay
    const swap = useRef(null);     // "paw" word-swap loop

    useEffect(() => {
      const el = root.current; if (!el) return;
      const reveal = () => el.classList.remove("hm-wait");
      if (!G || !window.SplitText) { reveal(); return; }

      let ctx = null, cancelled = false;
      // Split after the web font has loaded, so letters are measured with the real font.
      const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      fontsReady.then(() => {
        if (cancelled) return;
        ctx = G.context(() => {
          const mm = G.matchMedia();
          mm.add({ motion: "(prefers-reduced-motion: no-preference)", calm: "(prefers-reduced-motion: reduce)", small: "(max-width: 767px)" }, (c) => {
            reveal();
            if (c.conditions.calm) return;                       // final state, no animation

            const split = window.SplitText.create(el.querySelector(".hm-letters"), {
              type: "words,chars", charsClass: "hm-char", ignore: ".hm-dot, .hm-sw-alt", aria: "hidden",
            });
            const q = G.utils.selector(el);

            // Wordmark on its own timeline so it can be replayed on click.
            const wordTl = G.timeline({ paused: true })
              .from(split.chars, { yPercent: 70, rotate: 8, autoAlpha: 0, duration: 0.72, ease: "back.out(2.4)", stagger: 0.055 })
              .fromTo(q(".hm-dot"), { scale: 0, autoAlpha: 0 }, { scale: 1, autoAlpha: 1, duration: 0.9, ease: "elastic.out(1, 0.42)", transformOrigin: "50% 80%" }, "-=0.25");
            const wordDur = wordTl.duration();
            word.current = wordTl;

            // "paw" word swap. Widths are measured live (function values) so a resize re-measures.
            const acc = q(".hm-accent")[0], letters = q(".hm-letters")[0], copy = q(".hm-copy")[0];
            const layerEls = [q(".hm-sw-0")[0], ...SWAP.map((_, i) => q(".hm-sw-" + (i + 1))[0])];
            const layers = [q(".hm-sw-0 .hm-char"), ...SWAP.map((_, i) => q(".hm-swc-" + (i + 1)))];
            const wOf = (n) => layerEls[n].getBoundingClientRect().width / (G.getProperty(letters, "scale") || 1);
            const fit = (n) => {
              const full = letters.getBoundingClientRect().width / (G.getProperty(letters, "scale") || 1) - acc.getBoundingClientRect().width / (G.getProperty(letters, "scale") || 1) + wOf(n);
              return Math.min(1, (copy.clientWidth - 4) / full);
            };
            const off = { rotationX: -82, autoAlpha: 0, transformOrigin: "50% 100%", transformPerspective: 600 };
            G.set(acc, { width: wOf(0) });
            G.set(layers.slice(1), off);
            const swapTl = G.timeline({ paused: true, repeat: -1 });
            layers.forEach((cur, i) => {
              const n = (i + 1) % layers.length;
              swapTl
                .to(cur, { rotationX: 82, autoAlpha: 0, transformOrigin: "50% 0%", transformPerspective: 600, duration: 0.4, stagger: 0.05, ease: "power2.in" }, "+=2.2")
                .to(acc, { width: () => wOf(n), duration: 0.6, ease: "power3.inOut" }, "<0.1")
                .to(letters, { scale: () => fit(n), transformOrigin: "0% 60%", duration: 0.6, ease: "power3.inOut" }, "<")
                .fromTo(layers[n], off, { rotationX: 0, autoAlpha: 1, duration: 0.4, stagger: 0.05, ease: "power2.out", immediateRender: false }, "<0.15");
            });
            swap.current = swapTl;
            wordTl.add(() => swapTl.restart(), "+=0.8");
            let rz = 0, lastW = window.innerWidth;
            const onResize = () => { if (window.innerWidth === lastW) return; lastW = window.innerWidth; clearTimeout(rz); rz = setTimeout(() => { swapTl.pause(0); G.set(acc, { width: wOf(0) }); G.set(letters, { scale: 1 }); swapTl.invalidate().restart(); }, 250); };
            window.addEventListener("resize", onResize);

            // The two halves part and flip into the pet cards.
            // Desktop: pinned + scrubbed by scroll. Phones: the hero is shorter than the screen (pinning left a big
            // gap, and a scroll-scrub finished off-screen), so the flip plays by itself, in view, after the intro.
            const [left, right] = q(".hm-half");
            const splitTl = (vars) => G.timeline(vars)
              .to(q(".hm-front"), { borderRadius: 18, duration: 0.2 }, 0)
              .to(left, { x: 5, scale: 0.96, duration: 0.3, ease: "power2.out" }, 0)
              .to(right, { x: -5, scale: 0.96, duration: 0.3, ease: "power2.out" }, 0)
              .to(left, { rotationY: 180, rotationZ: 3, duration: 0.6, ease: "power2.inOut" }, 0.3)
              .to(right, { rotationY: 180, rotationZ: -3, duration: 0.6, ease: "power2.inOut" }, 0.42);
            if (c.conditions.small) {
              // phones: a short scroll down opens the picture into the pet cards (fully on screen); scrolling back
              // to the top closes it again. Plays as one smooth move rather than following the finger.
              const t = splitTl({ paused: true, defaults: { ease: "none" } }).timeScale(0.75);
              const art = q(".hm-art")[0] || el;
              if (window.ScrollTrigger) window.ScrollTrigger.create({ trigger: art, start: "top 20%", onEnter: () => t.play(), onLeaveBack: () => t.reverse() });
              else G.delayedCall(0.6 + wordDur + 1.2, () => t.play());
            } else if (window.ScrollTrigger) {
              const navH = () => { const n = document.querySelector(".nav"); return n ? n.offsetHeight : 0; };
              splitTl({ defaults: { ease: "none" }, scrollTrigger: { trigger: el, pin: true, start: () => "top " + (navH() + 8) + "px", end: "+=35%", scrub: 0.4, anticipatePin: 1, invalidateOnRefresh: true } });
              window.addEventListener("load", () => window.ScrollTrigger.refresh(), { once: true });
            }

            G.timeline({ defaults: { ease: "power3.out" } })
              .from(q(".hm-art img"), { y: 16, scale: 1.02, autoAlpha: 0, duration: 1.0 }, 0.3)
              .add(() => wordTl.play(0), 0.6)
              .add(() => tagIn.play(0), 0.6 + wordDur - 0.35);

            // Tagline flip: each letter tips over on its top edge while the next phrase rolls up.
            el.classList.add("hm-flipping");
            const L = TAG.map((_, i) => q(".hm-fc-" + i)), d = 0.4, s = 0.032, hold = "+=2.4";
            const hidden = { rotationX: -82, autoAlpha: 0, transformOrigin: "50% 100%" };
            const shown = { rotationX: 0, autoAlpha: 1, duration: d, stagger: s, ease: "power2.out", immediateRender: false };
            const out = { rotationX: 82, autoAlpha: 0, transformOrigin: "50% 0%", duration: d, stagger: s, ease: "power2.in" };
            G.set(L, hidden);
            const loop = G.timeline({ paused: true, repeat: -1 });
            L.forEach((cur, i) => loop
              .to(cur, out, hold)
              .fromTo(L[(i + 1) % L.length], hidden, shown, "<" + d * 0.62));
            const tagIn = G.timeline({ paused: true, onComplete: () => loop.play(0) })
              .from(q(".hm-flip-prefix"), { y: 8, autoAlpha: 0, duration: 0.45, ease: "power3.out" })
              .to(L[0], { rotationX: 0, autoAlpha: 1, duration: d, stagger: s, ease: "power2.out" });

            return () => { word.current = null; swap.current = null; clearTimeout(rz); window.removeEventListener("resize", onResize); el.classList.remove("hm-flipping"); };                // matchMedia/context revert the split + tweens
          });
        }, el);
      });
      // Safety net: never leave the hero hidden if something above fails.
      const t = setTimeout(reveal, 3000);
      return () => { cancelled = true; clearTimeout(t); if (ctx) ctx.revert(); };
    }, []);

    const replay = () => {
      const tl = word.current; if (!tl || tl.isActive()) return;
      if (swap.current) swap.current.pause(0);                   // back to "paw" before replaying
      tl.restart();
    };

    // Extra wrapper: ScrollTrigger wraps the pinned hero in a spacer, so the hero must not be React's root node.
    return h("div", { className: "hm-host" }, h("div", { ref: root, className: "hero-main hero-motion hm-wait" },
      h("div", { className: "hm-copy" },
        h("h1", { className: "hm-word" },
          h("span", { className: "visually-hidden" }, "ThePawtherapy"),
          h("button", { type: "button", className: "hm-word-btn", onClick: replay, "aria-label": "Replay logo animation", title: "Replay" },
            h("span", { className: "hm-letters", "aria-hidden": "true" },
              "the",
              h("span", { className: "hm-accent" },
                h("span", { className: "hm-sw-0" }, "paw"),
                SWAP.map((w, i) => h("span", { key: w, className: "hm-sw-alt hm-sw-" + (i + 1) },
                  Array.from(w).map((c, j) => h("span", { key: j, className: "hm-swc hm-swc-" + (i + 1), "data-c": c }))))),
              "therapy", h("span", { className: "hm-dot" }, ".")))
        ),
        h("p", { className: "hm-tag" },
          h("span", { className: "hm-tag-line" }, PREFIX + " " + TAG[0]),     // shown without motion
          h("span", { className: "visually-hidden" }, TAG.slice(1).map((t) => " " + PREFIX + " " + t).join("")),
          h("span", { className: "hm-flip", "aria-hidden": "true" },
            h("span", { className: "hm-flip-prefix" }, PREFIX + "\u00a0"),
            h("span", { className: "hm-flip-grid" },
              TAG.map((t, i) => h("span", { key: i, className: "hm-flip-layer" }, flipChars(t, i)))))
        )
      ),
      h("div", { className: "hm-art" },
        h("div", { className: "hm-split" },
          PET_CARDS.map((c, i) => {
            const n = petCount(c.pet);
            return h("button", {
              key: c.pet, type: "button", className: "hm-half hm-half-" + c.pet, "aria-label": c.cta,
              onClick: () => onNav && onNav({ page: "category", id: "all", pet: c.pet }),
            },
              h("span", { className: "hm-face hm-front" },
                h("img", { src: BASE + "paw-therapy-hero-banner.webp", alt: i ? "" : "A woman brushing her cat while a greyhound in an orange harness waits beside a lead, brush, towel and bowl", width: 808, height: 688, fetchPriority: "high", decoding: "async" })),
              h("span", { className: "hm-face hm-back", "aria-hidden": "true" },
                h("span", { className: "hm-card-ph" },
                  c.img ? h("img", { src: BASE + c.img, alt: c.alt || "", width: 720, height: 900, decoding: "async" })
                        : h("svg", { viewBox: "0 0 24 24", focusable: "false" }, h("path", { d: PAW_D }))),
                h("span", { className: "hm-card-k" }, c.kicker + (n ? " \u00b7 " + n + " products" : "")),
                h("span", { className: "hm-card-t" }, c.title),
                h("span", { className: "hm-card-d" }, c.text),
                h("span", { className: "hm-card-cta" }, c.cta + " \u2192")));
          }))
      )
    ));
  }

  window.PtMotionHero = PtMotionHero;
})();
